import React, {useEffect, useState} from 'react'
import {
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  Divider,
} from '@mui/material'
import surveysApi from '../../../services/api/surveys/surveysApi'
import WbOutlineButton from '../../../components/common/WbOutlineButton'
import WbButton from '../../../components/common/WbButton'
import WbAlert from '../../../components/common/WbAlert'
import {getApiHookParams} from '../../../auth'

export default function AddSchool({survey, onClose, onSuccess}) {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [schools, setSchools] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const apiParams = getApiHookParams()

  useEffect(() => {
    setLoading(true)
    surveysApi
      .getUnassignedSchools(survey.token)
      .then(res => setSchools(Array.isArray(res) ? res : []))
      .catch(() => setSchools([]))
      .finally(() => setLoading(false))
  }, [survey.token])

  const allSelected = schools.length > 0 && selectedIds.length === schools.length

  const toggleSchool = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : schools.map(s => s.id))
  }

  const handleAssign = () => {
    if (selectedIds.length === 0) return
    setSubmitting(true)
    const payload = {
      token: survey.token,
      schoolIds: selectedIds,
      groupId: apiParams.groupId ?? survey.matId ?? 0,
      schoolId: survey.schoolId ?? apiParams.schoolId ?? 0,
    }
    surveysApi
      .addSchoolsToLiveSurvey(payload)
      .then(() => {
        WbAlert({
          message:
            selectedIds.length > 1
              ? 'Schools added to the survey successfully'
              : 'School added to the survey successfully',
          type: 'success',
        })
        onSuccess()
        onClose()
      })
      .catch(e => {
        WbAlert({
          message: e?.message || 'Failed to add school. Please try again.',
          type: 'error',
        })
      })
      .finally(() => setSubmitting(false))
  }

  if (loading) {
    return (
      <Grid container justifyContent="center" sx={{py: 3}}>
        <CircularProgress />
      </Grid>
    )
  }

  if (schools.length === 0) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          All schools are already assigned to this survey.
        </Grid>
        <Grid item xs={12} style={{display: 'flex', justifyContent: 'flex-end'}}>
          <WbOutlineButton CustomOutlineButtonText="Close" onClick={onClose} />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" sx={{mb: 1}}>
          Select the school(s) to add to{' '}
          <Box component="span" fontWeight={600}>
            {survey.name}
          </Box>
          .
        </Typography>
        <Box
          sx={{
            border: '1px solid #E0E0E0',
            borderRadius: 1,
            maxHeight: 280,
            overflow: 'auto',
          }}
        >
          <List dense disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={toggleSelectAll} dense>
                <ListItemIcon sx={{minWidth: 36}}>
                  <Checkbox
                    edge="start"
                    checked={allSelected}
                    indeterminate={
                      selectedIds.length > 0 && selectedIds.length < schools.length
                    }
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary="Select all" />
              </ListItemButton>
            </ListItem>
            <Divider />
            {schools.map(school => (
              <ListItem key={school.id} disablePadding>
                <ListItemButton onClick={() => toggleSchool(school.id)} dense>
                  <ListItemIcon sx={{minWidth: 36}}>
                    <Checkbox
                      edge="start"
                      checked={selectedIds.includes(school.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText primary={school.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Invites are not sent automatically. Use “Resend Invites” to notify the
          newly added school(s).
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        style={{
          display: 'flex',
          textAlign: 'right',
          justifyContent: 'flex-end',
          gap: 8,
        }}
      >
        <WbOutlineButton
          CustomOutlineButtonText="Cancel"
          onClick={onClose}
          disabled={submitting}
        />
        <WbButton
          CustomButtonText={submitting ? 'Assigning…' : 'Assign'}
          onClick={handleAssign}
          disabled={submitting || selectedIds.length === 0}
        />
      </Grid>
    </Grid>
  )
}
